import { ContentStyle } from './contentStyleAnalyzer';

export interface DesignTokens {
  // Colors
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    shadow: string;
    highlight: string;
  };
  
  // Typography
  typography: {
    fontFamily: {
      heading: string;
      body: string;
      mono: string;
    };
    googleFonts: string[]; // Fonts to load from Google
    fontSize: {
      xs: string;
      sm: string;
      base: string;
      lg: string;
      xl: string;
      xxl: string;
    };
    fontWeight: {
      normal: number;
      medium: number;
      bold: number;
    };
    letterSpacing: {
      tight: string;
      normal: string;
      wide: string;
    };
  };
  
  // Spacing & Layout
  spacing: {
    borderRadius: {
      none: string;
      sm: string;
      md: string;
      lg: string;
      full: string;
    };
    gap: {
      sm: string;
      md: string;
      lg: string;
    };
  };
  
  // Effects
  effects: {
    boxShadow: {
      sm: string;
      md: string;
      lg: string;
      glow: string;
    };
    borderStyle: string;
    borderWidth: string;
    transition: string;
    hoverTransform: string;
    pattern?: string; // CSS pattern or texture
  };
  
  // Animations
  animations: {
    entrance: string;
    hover: string;
    loading: string;
  };
}

export const designSystems: Record<ContentStyle, DesignTokens> = {
  COMIC: {
    colors: {
      primary: '#F74E4E',
      secondary: '#2E4CFF',
      accent: '#FFD700',
      background: '#FFFFFF',
      surface: '#F8F8F8',
      text: '#1A1A1A',
      textSecondary: '#4A4A4A',
      border: '#000000',
      shadow: '#000000',
      highlight: '#FFEB3B'
    },
    typography: {
      fontFamily: {
        heading: '"Bebas Neue", sans-serif',
        body: '"Inter", sans-serif',
        mono: '"Fira Code", monospace'
      },
      googleFonts: ['Bebas Neue', 'Inter', 'Fira Code'],
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.25rem',
        xl: '1.75rem',
        xxl: '2.5rem'
      },
      fontWeight: {
        normal: 400,
        medium: 600,
        bold: 800
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.08em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '4px',
        md: '8px',
        lg: '16px',
        full: '9999px'
      },
      gap: {
        sm: '8px',
        md: '16px',
        lg: '24px'
      }
    },
    effects: {
      boxShadow: {
        sm: '2px 2px 0px #000',
        md: '4px 4px 0px #000',
        lg: '6px 6px 0px #000',
        glow: '0 0 20px rgba(255, 215, 0, 0.6)'
      },
      borderStyle: 'solid',
      borderWidth: '3px',
      transition: 'all 0.1s ease',
      hoverTransform: 'translate(-2px, -2px)',
      pattern: 'radial-gradient(circle, transparent 20%, #00000005 20%)'
    },
    animations: {
      entrance: 'bounceIn',
      hover: 'pulse',
      loading: 'shake'
    }
  },
  
  SCIFI: {
    colors: {
      primary: '#00F0FF',
      secondary: '#FF00F0',
      accent: '#00FF88',
      background: '#0A0A0F',
      surface: '#1A1A2E',
      text: '#E0E0FF',
      textSecondary: '#9090B0',
      border: '#00F0FF',
      shadow: '#00F0FF',
      highlight: '#FFFF00'
    },
    typography: {
      fontFamily: {
        heading: '"Exo 2", sans-serif',
        body: '"Inter", sans-serif',
        mono: '"Fira Code", monospace'
      },
      googleFonts: ['Exo 2', 'Inter', 'Fira Code'],
      fontSize: {
        xs: '0.7rem',
        sm: '0.85rem',
        base: '0.95rem',
        lg: '1.2rem',
        xl: '1.6rem',
        xxl: '2.2rem'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 700
      },
      letterSpacing: {
        tight: '0',
        normal: '0.04em',
        wide: '0.12em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '2px',
        md: '4px',
        lg: '8px',
        full: '0'
      },
      gap: {
        sm: '12px',
        md: '20px',
        lg: '32px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 0 10px rgba(0, 240, 255, 0.5)',
        md: '0 0 20px rgba(0, 240, 255, 0.6)',
        lg: '0 0 30px rgba(0, 240, 255, 0.7)',
        glow: '0 0 40px rgba(0, 240, 255, 0.8), inset 0 0 20px rgba(0, 240, 255, 0.2)'
      },
      borderStyle: 'solid',
      borderWidth: '1px',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      hoverTransform: 'scale(1.02)',
      pattern: 'linear-gradient(0deg, transparent 24%, #00F0FF08 25%, #00F0FF08 26%, transparent 27%, transparent 74%, #00F0FF08 75%, #00F0FF08 76%, transparent 77%, transparent)'
    },
    animations: {
      entrance: 'fadeInUp',
      hover: 'glow',
      loading: 'pulse'
    }
  },
  
  ROMANCE: {
    colors: {
      primary: '#FF6B9D',
      secondary: '#FEC8D8',
      accent: '#FFDFD3',
      background: '#FFF5F7',
      surface: '#FFFFFF',
      text: '#4A3C3C',
      textSecondary: '#8A7575',
      border: '#FFB8CC',
      shadow: '#FF6B9D',
      highlight: '#FFD700'
    },
    typography: {
      fontFamily: {
        heading: '"Playfair Display", serif',
        body: '"Inter", sans-serif',
        mono: '"Courier New", monospace'
      },
      googleFonts: ['Playfair Display', 'Inter'],
      fontSize: {
        xs: '0.8rem',
        sm: '0.9rem',
        base: '1rem',
        lg: '1.3rem',
        xl: '1.8rem',
        xxl: '2.4rem'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 600
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.06em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '12px',
        md: '20px',
        lg: '30px',
        full: '9999px'
      },
      gap: {
        sm: '10px',
        md: '18px',
        lg: '28px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 2px 8px rgba(255, 107, 157, 0.15)',
        md: '0 4px 16px rgba(255, 107, 157, 0.2)',
        lg: '0 8px 24px rgba(255, 107, 157, 0.25)',
        glow: '0 0 30px rgba(255, 107, 157, 0.3)'
      },
      borderStyle: 'solid',
      borderWidth: '2px',
      transition: 'all 0.4s ease-in-out',
      hoverTransform: 'translateY(-4px) rotate(-1deg)',
      pattern: 'radial-gradient(circle at 20% 80%, #FF6B9D10 0%, transparent 50%)'
    },
    animations: {
      entrance: 'heartBeat',
      hover: 'float',
      loading: 'bounce'
    }
  },
  
  FANTASY: {
    colors: {
      primary: '#8B4513',
      secondary: '#DAA520',
      accent: '#4B0082',
      background: '#F4E8D0',
      surface: '#FFF8E7',
      text: '#2C1810',
      textSecondary: '#5D4037',
      border: '#8B6914',
      shadow: '#654321',
      highlight: '#FFD700'
    },
    typography: {
      fontFamily: {
        heading: '"Libre Baskerville", serif',
        body: '"Inter", sans-serif',
        mono: '"Courier New", monospace'
      },
      googleFonts: ['Libre Baskerville', 'Inter'],
      fontSize: {
        xs: '0.8rem',
        sm: '0.9rem',
        base: '1.05rem',
        lg: '1.35rem',
        xl: '1.9rem',
        xxl: '2.6rem'
      },
      fontWeight: {
        normal: 400,
        medium: 600,
        bold: 700
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.08em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '4px',
        md: '8px',
        lg: '12px',
        full: '0'
      },
      gap: {
        sm: '12px',
        md: '20px',
        lg: '32px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 2px 4px rgba(101, 67, 33, 0.3)',
        md: '0 4px 8px rgba(101, 67, 33, 0.4)',
        lg: '0 8px 16px rgba(101, 67, 33, 0.5)',
        glow: '0 0 20px rgba(218, 165, 32, 0.4)'
      },
      borderStyle: 'double',
      borderWidth: '3px',
      transition: 'all 0.5s ease',
      hoverTransform: 'scale(1.03)',
      pattern: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%239C92AC" fill-opacity="0.03"%3E%3Cpath d="M20 20L10 10h10z"/%3E%3C/g%3E%3C/svg%3E")'
    },
    animations: {
      entrance: 'fadeIn',
      hover: 'shimmer',
      loading: 'rotate'
    }
  },
  
  HORROR: {
    colors: {
      primary: '#8B0000',
      secondary: '#2F1B1B',
      accent: '#FF0000',
      background: '#0A0A0A',
      surface: '#1A1A1A',
      text: '#E0E0E0',
      textSecondary: '#999999',
      border: '#660000',
      shadow: '#000000',
      highlight: '#FF6666'
    },
    typography: {
      fontFamily: {
        heading: '"Oswald", sans-serif',
        body: '"Inter", sans-serif',
        mono: '"Courier New", monospace'
      },
      googleFonts: ['Oswald', 'Inter'],
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.25rem',
        xl: '1.75rem',
        xxl: '2.5rem'
      },
      fontWeight: {
        normal: 300,
        medium: 400,
        bold: 700
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.1em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '2px',
        md: '4px',
        lg: '8px',
        full: '0'
      },
      gap: {
        sm: '8px',
        md: '16px',
        lg: '24px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 2px 8px rgba(139, 0, 0, 0.5)',
        md: '0 4px 16px rgba(139, 0, 0, 0.6)',
        lg: '0 8px 32px rgba(139, 0, 0, 0.7)',
        glow: '0 0 30px rgba(255, 0, 0, 0.3)'
      },
      borderStyle: 'solid',
      borderWidth: '1px',
      transition: 'all 0.6s ease-in-out',
      hoverTransform: 'scale(0.98)',
      pattern: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #8B000005 10px, #8B000005 20px)'
    },
    animations: {
      entrance: 'fadeInDown',
      hover: 'flicker',
      loading: 'throb'
    }
  },
  
  RETRO: {
    colors: {
      primary: '#FF6B35',
      secondary: '#F7931E',
      accent: '#FCE029',
      background: '#2A1A3E',
      surface: '#44318D',
      text: '#FFFFFF',
      textSecondary: '#D8BFD8',
      border: '#A64AC9',
      shadow: '#17E9E0',
      highlight: '#FCCD04'
    },
    typography: {
      fontFamily: {
        heading: '"Archivo Black", sans-serif',
        body: '"Inter", sans-serif',
        mono: '"Fira Code", monospace'
      },
      googleFonts: ['Archivo Black', 'Inter', 'Fira Code'],
      fontSize: {
        xs: '0.8rem',
        sm: '0.9rem',
        base: '1rem',
        lg: '1.3rem',
        xl: '1.8rem',
        xxl: '2.8rem'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 700
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.08em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '8px',
        md: '16px',
        lg: '24px',
        full: '9999px'
      },
      gap: {
        sm: '10px',
        md: '18px',
        lg: '28px'
      }
    },
    effects: {
      boxShadow: {
        sm: '3px 3px 0px #17E9E0',
        md: '5px 5px 0px #17E9E0',
        lg: '8px 8px 0px #17E9E0',
        glow: '0 0 20px #FCE029'
      },
      borderStyle: 'solid',
      borderWidth: '3px',
      transition: 'all 0.2s ease',
      hoverTransform: 'translate(-3px, -3px)',
      pattern: 'linear-gradient(45deg, #FF6B3540 25%, transparent 25%), linear-gradient(-45deg, #FF6B3540 25%, transparent 25%)'
    },
    animations: {
      entrance: 'zoomIn',
      hover: 'jiggle',
      loading: 'spin'
    }
  },
  
  ANIME: {
    colors: {
      primary: '#FF69B4',
      secondary: '#87CEEB',
      accent: '#FFB6C1',
      background: '#FFF0F5',
      surface: '#FFFFFF',
      text: '#4B0082',
      textSecondary: '#8B008B',
      border: '#FF1493',
      shadow: '#FF69B4',
      highlight: '#FFD700'
    },
    typography: {
      fontFamily: {
        heading: '"Quicksand", sans-serif',
        body: '"Inter", sans-serif',
        mono: '"Courier New", monospace'
      },
      googleFonts: ['Quicksand', 'Inter'],
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.25rem',
        xl: '1.75rem',
        xxl: '2.5rem'
      },
      fontWeight: {
        normal: 400,
        medium: 600,
        bold: 800
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.06em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '12px',
        md: '20px',
        lg: '30px',
        full: '9999px'
      },
      gap: {
        sm: '8px',
        md: '16px',
        lg: '24px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 2px 8px rgba(255, 105, 180, 0.3)',
        md: '0 4px 16px rgba(255, 105, 180, 0.4)',
        lg: '0 8px 24px rgba(255, 105, 180, 0.5)',
        glow: '0 0 30px rgba(255, 182, 193, 0.6)'
      },
      borderStyle: 'solid',
      borderWidth: '2px',
      transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
      hoverTransform: 'scale(1.05) rotate(2deg)',
      pattern: 'radial-gradient(circle, #FF69B410 1px, transparent 1px)'
    },
    animations: {
      entrance: 'bounceInRight',
      hover: 'wiggle',
      loading: 'bounce'
    }
  },
  
  MYSTERY: {
    colors: {
      primary: '#4A5568',
      secondary: '#2D3748',
      accent: '#718096',
      background: '#1A202C',
      surface: '#2D3748',
      text: '#E2E8F0',
      textSecondary: '#A0AEC0',
      border: '#4A5568',
      shadow: '#000000',
      highlight: '#F7FAFC'
    },
    typography: {
      fontFamily: {
        heading: '"Playfair Display", serif',
        body: '"Source Sans Pro", sans-serif',
        mono: '"Fira Code", monospace'
      },
      googleFonts: ['Playfair Display', 'Source Sans Pro', 'Fira Code'],
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.25rem',
        xl: '1.75rem',
        xxl: '2.25rem'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 700
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.08em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '4px',
        md: '8px',
        lg: '12px',
        full: '0'
      },
      gap: {
        sm: '10px',
        md: '18px',
        lg: '28px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 1px 3px rgba(0, 0, 0, 0.6)',
        md: '0 4px 6px rgba(0, 0, 0, 0.7)',
        lg: '0 10px 15px rgba(0, 0, 0, 0.8)',
        glow: 'inset 0 0 20px rgba(113, 128, 150, 0.1)'
      },
      borderStyle: 'solid',
      borderWidth: '1px',
      transition: 'all 0.4s ease',
      hoverTransform: 'translateY(-2px)',
      pattern: 'repeating-linear-gradient(90deg, transparent, transparent 2px, #4A556808 2px, #4A556808 4px)'
    },
    animations: {
      entrance: 'fadeIn',
      hover: 'reveal',
      loading: 'pulse'
    }
  },
  
  ACADEMIC: {
    colors: {
      primary: '#1E40AF',
      secondary: '#3B82F6',
      accent: '#60A5FA',
      background: '#FAFAFA',
      surface: '#FFFFFF',
      text: '#111827',
      textSecondary: '#6B7280',
      border: '#E5E7EB',
      shadow: '#9CA3AF',
      highlight: '#DBEAFE'
    },
    typography: {
      fontFamily: {
        heading: '"Libre Baskerville", serif',
        body: '"Inter", sans-serif',
        mono: '"JetBrains Mono", monospace'
      },
      googleFonts: ['Libre Baskerville', 'Inter', 'JetBrains Mono'],
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.5rem',
        xxl: '2rem'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 700
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.04em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '4px',
        md: '6px',
        lg: '8px',
        full: '0'
      },
      gap: {
        sm: '8px',
        md: '16px',
        lg: '24px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
        md: '0 4px 6px rgba(0, 0, 0, 0.07)',
        lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
        glow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
      },
      borderStyle: 'solid',
      borderWidth: '1px',
      transition: 'all 0.2s ease',
      hoverTransform: 'translateY(-1px)',
      pattern: 'none'
    },
    animations: {
      entrance: 'slideInUp',
      hover: 'lift',
      loading: 'progress'
    }
  },
  
  DEFAULT: {
    colors: {
      primary: '#6366f1',
      secondary: '#4f46e5',
      accent: '#818cf8',
      background: '#0f172a',
      surface: '#1e293b',
      text: '#f1f5f9',
      textSecondary: '#94a3b8',
      border: '#334155',
      shadow: '#000000',
      highlight: '#6366f1'
    },
    typography: {
      fontFamily: {
        heading: '"Bebas Neue", "Russo One", "Arial Black", sans-serif',
        body: '"Rajdhani", "Space Mono", "Georgia", serif',
        mono: '"Space Mono", "Courier New", monospace'
      },
      googleFonts: ['Bebas Neue', 'Rajdhani', 'Space Mono', 'Russo One'],
      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.25rem',
        xl: '1.75rem',
        xxl: '2.5rem'
      },
      fontWeight: {
        normal: 400,
        medium: 500,
        bold: 700
      },
      letterSpacing: {
        tight: '0',
        normal: '0.02em',
        wide: '0.06em'
      }
    },
    spacing: {
      borderRadius: {
        none: '0',
        sm: '4px',
        md: '8px',
        lg: '16px',
        full: '9999px'
      },
      gap: {
        sm: '8px',
        md: '16px',
        lg: '24px'
      }
    },
    effects: {
      boxShadow: {
        sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
        md: '0 4px 6px rgba(0, 0, 0, 0.1)',
        lg: '0 10px 20px rgba(0, 0, 0, 0.15)',
        glow: '0 0 20px rgba(74, 144, 226, 0.3)'
      },
      borderStyle: 'solid',
      borderWidth: '1px',
      transition: 'all 0.3s ease',
      hoverTransform: 'translateY(-2px)',
      pattern: 'none'
    },
    animations: {
      entrance: 'fadeIn',
      hover: 'lift',
      loading: 'spin'
    }
  }
};